import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useHasActiveCampaign } from "@/hooks/useActiveCampaign";
import { useWrappedEnabled } from "@/hooks/useWrappedEnabled";
import { useCircleConfig } from "@/hooks/useCircleConfig";
import { useTracking } from "@/hooks/useInteractionTracking";
import { Calendar, User, Home, UserCog, Sparkles, Gift, Users2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const Navigation = () => {
  const location = useLocation();
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const { hasActiveCampaign, isLoading: isCampaignLoading } = useHasActiveCampaign();
  const { isWrappedEnabled, isLoading: isWrappedLoading } = useWrappedEnabled();
  const { config: circleConfig, isLoading: isCircleLoading } = useCircleConfig();
  const { trackClick } = useTracking();
  const isActive = (path: string) => location.pathname === path;

  // On touch devices the mobile keyboard pushes fixed bottom bars up over the
  // page content. Nobody navigates while typing, so slide the nav out of the
  // way whenever a text field is focused. Desktop (fine pointer) is unaffected.
  const [isTyping, setIsTyping] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(pointer: coarse)").matches) return;
    const isEditable = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    const onFocusIn = (e: FocusEvent) => setIsTyping(isEditable(e.target));
    const onFocusOut = (e: FocusEvent) => {
      // Keep the nav hidden if focus simply moved to another input.
      if (!isEditable(e.relatedTarget)) setIsTyping(false);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // Only show conditional buttons when loaded and enabled
  const showWrapped = !isWrappedLoading && isWrappedEnabled;
  const showOffers = !isCampaignLoading && hasActiveCampaign;
  const showCircle = !isCircleLoading && circleConfig.enabled;
  const showAdmin = !isAdminLoading && isAdmin;

  const handleNavClick = (navItem: string, path: string) => {
    trackClick(`nav_${navItem}`, { destination: path });
  };
  
  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 bg-primary border-t border-primary-foreground/10 px-4 py-2 transition-transform duration-300 ${
        isTyping ? "translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="flex justify-around items-center max-w-md mx-auto">
        <Link
          to="/home"
          onClick={() => handleNavClick("home", "/home")}
          className={`flex flex-col items-center ${
            isActive("/home") ? "text-secondary" : "text-primary-foreground/70"
          }`}
        >
          <Home size={24} />
          <span className="text-xs mt-1">Home</span>
        </Link>
        <Link
          to="/events"
          onClick={() => handleNavClick("events", "/events")}
          className={`flex flex-col items-center ${
            isActive("/events") ? "text-secondary" : "text-primary-foreground/70"
          }`}
        >
          <Calendar size={24} />
          <span className="text-xs mt-1">Originals</span>
        </Link>
        {showCircle && (
          <Link
            to="/circle"
            onClick={() => handleNavClick("circle", "/circle")}
            className={`flex flex-col items-center ${
              isActive("/circle") ? "text-secondary" : "text-primary-foreground/70"
            }`}
          >
            <Users2 size={24} />
            <span className="text-xs mt-1">Circle</span>
          </Link>
        )}
        {showWrapped && (
          <Link
            to="/wrapped"
            onClick={() => handleNavClick("wrapped", "/wrapped")}
            className={`flex flex-col items-center ${
              isActive("/wrapped") ? "text-secondary" : "text-primary-foreground/70"
            }`}
          >
            <Gift size={24} />
            <span className="text-xs mt-1">Wrapped</span>
          </Link>
        )}
        {showOffers && (
          <Link
            to="/offers"
            onClick={() => handleNavClick("offers", "/offers")}
            className={`flex flex-col items-center ${
              isActive("/offers") ? "text-secondary" : "text-primary-foreground/70"
            }`}
          >
            <Sparkles size={24} />
            <span className="text-xs mt-1">Offers</span>
          </Link>
        )}
        <Link
          to="/profile"
          onClick={() => handleNavClick("profile", "/profile")}
          className={`flex flex-col items-center ${
            isActive("/profile") ? "text-secondary" : "text-primary-foreground/70"
          }`}
        >
          <User size={24} />
          <span className="text-xs mt-1">Profile</span>
        </Link>
        {showAdmin && (
          <Link
            to="/admin"
            onClick={() => handleNavClick("admin", "/admin")}
            className={`flex flex-col items-center ${
              isActive("/admin") ? "text-secondary" : "text-primary-foreground/70"
            }`}
          >
            <UserCog size={24} />
            <span className="text-xs mt-1">Admin</span>
          </Link>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
